import React, { createContext, useState, useEffect, useContext } from "react";
import { fetchData, apiClient, isLikelyJwt, setBearerToken } from "../api/client";
import { readPrimaryProjectId } from "../utils/primaryProjectId";
import throttle from "lodash/throttle";
import {
  unifiedLogout,
  registerLogoutHandler,
  isUnauthorizedLogoutSuppressed,
} from "../services/logoutService";
import { setUiLocale, readStoredLocale } from "../i18n/config";
import {
  readWorkspaceSession,
  writeWorkspaceSession,
  WS_KEY,
} from "../utils/workspaceStorage";
import {
  readTabBearerToken,
  readTabUserData,
  readTabLastActivity,
  readTabTokenExpiration,
  writeTabUserData,
  writeTabTokenExpiration,
  writeTabLastActivity,
  writeTabAuthWorkspaceSlug,
} from "../utils/tabAuthStorage";
import { getActiveWorkspaceSlug, normalizeWorkspaceSlug } from "../utils/workspaceStorage";
import { readJwtCompanyClaim, readJwtScope } from "../api/clientJwt";

export const AuthContext = createContext();
/** Janela local para disparar /refresh-token (JWT real é 7d). Curta demais colide com ops longas (Migrar todos). */
const expiresIn = 3600;
const refreshSkewMs = 5 * 60 * 1000;
const inactivityLimit = 3000; // 5 minutos em milissegundos
let refreshInFlight = null;

/** Chrome/Safari: WebAuthn sem gesto válido, prompt fechado ou gerenciador de senhas aberto. */
export function isWebAuthnGestureDenied(err) {
  const name = String(err?.name || "");
  const msg = String(err?.message || "");
  return (
    name === "NotAllowedError" ||
    name === "AbortError" ||
    /not allowed by the user agent/i.test(msg) ||
    /user denied permission/i.test(msg)
  );
}

function isTransientRefreshError(error) {
  const status = error?.status ?? error?.response?.status;
  const code = error?.code ?? error?.response?.code;
  if (status == null) return true;
  if (status >= 500) return true;
  if (code === "ECONNABORTED" || code === "ERR_NETWORK") return true;
  const msg = String(error?.message || "");
  return /timeout|timed?\s*out|Network Error|ECONNABORTED|ERR_NETWORK/i.test(msg);
}

export const AuthProvider = ({ children }) => {

  const [bearerToken, setBearerTokenState] = useState(() => readTabBearerToken());
  const [userData, setUserData] = useState(() => readTabUserData());
  const [lastActivity, setLastActivity] = useState(
    () => readTabLastActivity() || Date.now()
  );
  const [status, setStatus] = useState(
    readWorkspaceSession(WS_KEY.USER_STATUS) || "Automático"
  );
  const [statusLocked, setStatusLocked] = useState(false); 

  const [redirectPath, setRedirectPath] = useState(null);

  // useEffect(() => {
  //   if (["Automático", "Ausente", "Não perturbe"].includes(status)) {
  //     sessionStorage.setItem("status", status);
  //   }
  // }, [status]);



  useEffect(() => {
    // registra como limpar os estados React quando o logout unificado for chamado
    registerLogoutHandler(() => {
      setBearerTokenState(null);
      setUserData(null);
      setRedirectPath(null);
      // status visual pode ficar "Automático" na próxima sessão
      // (se quiser, pode forçar aqui: setStatus("Automático"); setStatusLocked(false);)
    });
  }, []);
  
  const updateUserData = (partial) => {
    setUserData((prev) => {
      const next = { ...(prev || {}), ...(partial || {}) };
      try {
        writeTabUserData(next);
      } catch (e) {
        console.warn("Falha ao persistir userData nesta aba:", e);
      }
      return next;
    });
  };
  
  /** Persiste JWT-derived claims (`scope`, `resellerId`, `master`, …) na sessão desta aba. */
  const login = (token, user) => {
    if (!isLikelyJwt(token)) {
      console.warn("login ignorado: token JWT inválido.");
      return;
    }
    const normalized = token.trim();
    setBearerToken(normalized);
    setBearerTokenState(normalized);

    const scope = readJwtScope(normalized) || user?.scope || "workspace";
    const claimCompany = readJwtCompanyClaim(normalized);
    const activeSlug = normalizeWorkspaceSlug(
      claimCompany || user?.company || getActiveWorkspaceSlug()
    );
    if (scope === "workspace" && activeSlug) {
      writeTabAuthWorkspaceSlug(activeSlug);
    } else {
      writeTabAuthWorkspaceSlug(null);
    }

    if (user) {
      const incoming = { ...(user || {}), scope };
      if (activeSlug && scope === "workspace") incoming.company = activeSlug;
      setUserData(incoming);
      try {
        writeTabUserData(incoming);
      } catch (e) {
        console.warn("Falha ao persistir userData nesta aba durante login:", e);
      }
      const lc = user?.locale_code != null ? String(user.locale_code).trim() : "";
      if (lc) {
        setUiLocale(lc);
      } else if (!readStoredLocale()) {
        const pid = readPrimaryProjectId();
        apiClient
          .get("/i18n/resolved-ui-locale", { params: pid ? { id_projeto: pid } : {} })
          .then((res) => {
            const loc = res.data?.data?.locale;
            if (loc && typeof loc === "string") setUiLocale(loc);
          })
          .catch(() => {});
      }
    } else {
      console.warn("userData está vazio ou inválido.");
    }

    resetActivity();
    const expirationTime = Date.now() + expiresIn * 1000;
    writeTabTokenExpiration(expirationTime.toString());
  };

  const logout = async () => {
    // Agora usa o logout unificado
    await unifiedLogout("manual");
  };

  const refreshToken = async () => {
    if (refreshInFlight) return refreshInFlight;
    refreshInFlight = (async () => {
      try {
        const response = await fetchData("/refresh-token", "POST", null, { timeout: 120000 });
        const { token } = response.data;
        if (token) {
          setBearerToken(token);
          setBearerTokenState(token);
          const expirationTime = Date.now() + expiresIn * 1000;
          writeTabTokenExpiration(expirationTime.toString());
          try {
            const scope = readJwtScope(token);
            const claimCompany = readJwtCompanyClaim(token);
            if (scope === "workspace" && claimCompany) {
              writeTabAuthWorkspaceSlug(claimCompany);
            }
          } catch {
            /* ignore */
          }
        }
      } catch (error) {
        // Timeout/rede com API ocupada (ex. Migrar todos) não deve deslogar nem dar beacon /logout.
        if (isTransientRefreshError(error)) {
          console.warn("Renovação de token adiada (erro transitório):", error?.message || error);
          const current = Number(readTabTokenExpiration()) || 0;
          const retryAt = Date.now() + 90 * 1000;
          if (!current || current < retryAt) {
            writeTabTokenExpiration(String(retryAt));
          }
          return;
        }
        const status = error?.status ?? error?.response?.status;
        if ((status === 401 || status === 403) && !isUnauthorizedLogoutSuppressed()) {
          console.error("Erro ao renovar o token (sessão inválida):", error);
          await unifiedLogout("refresh_failed");
        } else {
          console.warn("Erro ao renovar o token (não fatal):", error?.message || error);
        }
      } finally {
        refreshInFlight = null;
      }
    })();
    return refreshInFlight;
  };

  const resetActivity = () => {
    const now = Date.now();
    setLastActivity(now);
    writeTabLastActivity(now.toString());
    // console.log('resetActivity', status, statusLocked)
    if (status === "Ausente" && !statusLocked) {
      writeWorkspaceSession(WS_KEY.USER_STATUS, "Automático");
      setStatus("Automático"); // Só altera o status se não estiver bloqueado
    }
  };

  const handleStatusChange = (newStatus) => {
    writeWorkspaceSession(WS_KEY.USER_STATUS, newStatus);
    setStatus(newStatus);
    setStatusLocked(newStatus !== "Automático"); // Bloqueia status se for manual
  };

  const prepareBiometricChallenge = async (email) => {
    const responseChallenge = await fetchData("/biometric-challenge", "POST", { email });
    const challenge = responseChallenge?.data?.challenge;
    if (!challenge) {
      throw new Error("Não foi possível obter o challenge biométrico.");
    }
    return challenge;
  };

  const registerBiometric = async (email, { challenge: prefetchedChallenge } = {}) => {
    try {
      const challenge = prefetchedChallenge || (await prepareBiometricChallenge(email));

      const decodeBase64URL = (base64url) => {
        return atob(base64url.replace(/-/g, "+").replace(/_/g, "/"));
      };
  
      // Configura as opções para registro biométrico
      const publicKeyCredentialCreationOptions = {
        challenge: Uint8Array.from(decodeBase64URL(challenge), (c) => c.charCodeAt(0)),
        rp: {
          name: "Xpro",
          id: window.location.hostname,
        },
        user: {
          id: Uint8Array.from(btoa(email), (c) => c.charCodeAt(0)),
          name: email,
          displayName: email,
        },
        pubKeyCredParams: [{ alg: -7, type: "public-key" }], // ES256
        authenticatorSelection: {
          userVerification: "required",
        },
      };
      // console.log(window.location.hostname, publicKeyCredentialCreationOptions)
  
      // Cria a credencial biométrica no navegador
      const credential = await navigator.credentials.create({
        publicKey: publicKeyCredentialCreationOptions,
      });
  
      // Converte os dados da credencial para o formato esperado pela API
      const registrationResponse = {
        id: credential.id,
        rawId: btoa(String.fromCharCode(...new Uint8Array(credential.rawId))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, ''),
        type: credential.type,
        response: {
          attestationObject: btoa(String.fromCharCode(...new Uint8Array(credential.response.attestationObject))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, ''),
          clientDataJSON: btoa(String.fromCharCode(...new Uint8Array(credential.response.clientDataJSON))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, ''),
        },
      };      
  
      // Envia a credencial biométrica para registro no servidor
      const response = await fetchData("/register-biometric", "POST", {
        email,
        registrationResponse,
      });
  
      if (response?.data) {
        alert("Credencial biométrica registrada com sucesso!");
        localStorage.setItem("biometricId", credential.id);
      } else {
        throw new Error("Erro ao registrar credencial biométrica.");
      }
    } catch (err) {
      console.error("Erro ao registrar credencial biométrica:", err);
      throw err;
    }
  };  

  const biometricLogin = async (email) => {
    try {
      // Verifica se o desafio já está armazenado no localStorage/sessionStorage
      let cachedChallenge = localStorage.getItem("biometricChallenge");
  
      // Se não houver um desafio em cache, solicite um novo
      if (!cachedChallenge) {
        const responseChallenge = await fetchData("/biometric-challenge", "POST", { email });
        cachedChallenge = responseChallenge?.data?.challenge;
  
        if (!cachedChallenge) {
          throw new Error("Não foi possível obter o challenge biométrico.");
        }
  
        // Armazena o desafio no localStorage/sessionStorage
        localStorage.setItem("biometricChallenge", cachedChallenge);
      }
  
      // Recupera a credencial biométrica do localStorage
      const biometricId = localStorage.getItem("biometricId");
      if (!biometricId) {
        throw new Error("Nenhuma credencial biométrica registrada.");
      }
  
      // Configura as opções para autenticação biométrica
      const decodeBase64URL = (base64url) => {
        return atob(base64url.replace(/-/g, "+").replace(/_/g, "/"));
      };
  
      const options = {
        publicKey: {
          challenge: Uint8Array.from(decodeBase64URL(cachedChallenge), (c) => c.charCodeAt(0)),
          rpId: window.location.hostname,
          allowCredentials: [
            {
              id: Uint8Array.from(decodeBase64URL(biometricId), (c) => c.charCodeAt(0)),
              type: "public-key",
            },
          ],
          userVerification: "required",
        },
      };
  
      // Obtém a resposta da credencial biométrica
      const assertion = await navigator.credentials.get(options);
  
      // Formata a resposta para o backend
      const authenticationResponse = {
        id: assertion.id,
        rawId: btoa(String.fromCharCode(...new Uint8Array(assertion.rawId)))
          .replace(/\+/g, "-")
          .replace(/\//g, "_")
          .replace(/=+$/, ""),
        type: assertion.type,
        response: {
          authenticatorData: btoa(
            String.fromCharCode(...new Uint8Array(assertion.response.authenticatorData))
          )
            .replace(/\+/g, "-")
            .replace(/\//g, "_")
            .replace(/=+$/, ""),
          clientDataJSON: btoa(
            String.fromCharCode(...new Uint8Array(assertion.response.clientDataJSON))
          )
            .replace(/\+/g, "-")
            .replace(/\//g, "_")
            .replace(/=+$/, ""),
          signature: btoa(
            String.fromCharCode(...new Uint8Array(assertion.response.signature))
          )
            .replace(/\+/g, "-")
            .replace(/\//g, "_")
            .replace(/=+$/, ""),
        },
      };
  
      // Envia os dados para o backend
      const response = await fetchData("/biometric-login", "POST", {
        email,
        authenticationResponse,
      });
  
      // Verifica a resposta do backend
      if (response?.data?.token) {
        // Remove o desafio do cache após a autenticação
        localStorage.removeItem("biometricChallenge");
        return response;
      } else {
        throw new Error("Autenticação biométrica falhou.");
      }
    } catch (err) {
      console.error("Erro na autenticação biométrica:", err);
  
      // Remove o desafio do cache em caso de falha
      localStorage.removeItem("biometricChallenge");
      throw err;
    }
  };  

  // Perfil com locale explícito, ou política workspace quando locale_code é null e não há escolha no localStorage.
  useEffect(() => {
    if (!userData?.id) return;
    const lc = userData.locale_code != null ? String(userData.locale_code).trim() : "";
    if (lc) {
      if (!readStoredLocale()) setUiLocale(lc);
      return;
    }
    if (readStoredLocale()) return;
    const pid = readPrimaryProjectId();
    let cancelled = false;
    apiClient
      .get("/i18n/resolved-ui-locale", { params: pid ? { id_projeto: pid } : {} })
      .then((res) => {
        if (cancelled) return;
        const loc = res.data?.data?.locale;
        if (loc && typeof loc === "string") setUiLocale(loc);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [userData?.locale_code, userData?.id]);

  useEffect(() => {
    const checkTokenExpiration = () => {
      const tokenExpiration = Number(readTabTokenExpiration());

      // Renova com folga antes do fim da janela local (JWT no servidor continua válido por dias).
      if (tokenExpiration && tokenExpiration - Date.now() <= refreshSkewMs) {
        refreshToken();
      }
    };

    // Configura um intervalo para verificar periodicamente o token
    const interval = setInterval(checkTokenExpiration, 60 * 1000); // Verifica a cada 1 minuto

    return () => clearInterval(interval); // Limpa o intervalo ao desmontar o componente
  }, []);

  useEffect(() => {
    const handleActivity = throttle(() => {
      resetActivity();
    }, 1000); // Executa no máximo uma vez por segundo
  
    window.addEventListener("mousemove", handleActivity);
    window.addEventListener("keydown", handleActivity);
  
    return () => {
      window.removeEventListener("mousemove", handleActivity);
      window.removeEventListener("keydown", handleActivity);
    };
    // resetActivity é estável; incluir nas deps não é necessário
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const checkInactivity = () => {
      const now = Date.now();
      // console.log('checkInactivity', (now - lastActivity >= inactivityLimit && status === "Automático" && !statusLocked), status, !statusLocked);
      if (now - lastActivity >= inactivityLimit && status === "Automático" && !statusLocked) {
        writeWorkspaceSession(WS_KEY.USER_STATUS, "Ausente");
        setStatus("Ausente");
      }
    };

    const interval = setInterval(checkInactivity, 10000);

    return () => clearInterval(interval);
  }, [lastActivity, status, statusLocked]);
  

  // useEffect(() => {
  //   const handler = () => {
  //     try {
  //       const token = localStorage.getItem("bearerToken");
  //       if (!token) return;

  //       const url = `${process.env.NEXT_PUBLIC_API_BASE_URL || ""}/logout`;
  //       // Monta um payload simples; o backend só precisa do Authorization.
  //       const blob = new Blob([JSON.stringify({ reason: "window_unload" })], { type: "application/json" });

  //       // Envia o beacon com Authorization usando fetch via keepalive se sendBeacon não suportar headers
  //       if (navigator.sendBeacon) {
  //         // sendBeacon não aceita custom headers; então usamos fetch keepalive com Authorization
  //         fetch(url, {
  //           method: "POST",
  //           keepalive: true,
  //           headers: {
  //             "Content-Type": "application/json",
  //             Authorization: `Bearer ${token}`,
  //           },
  //           body: JSON.stringify({ reason: "window_unload" }),
  //         }).catch(() => {});
  //       } else {
  //         fetch(url, {
  //           method: "POST",
  //           keepalive: true,
  //           headers: {
  //             "Content-Type": "application/json",
  //             Authorization: `Bearer ${token}`,
  //           },
  //           body: JSON.stringify({ reason: "window_unload" }),
  //         }).catch(() => {});
  //       }
  //     } catch {}
  //   };

  //   window.addEventListener("beforeunload", handler);
  //   return () => window.removeEventListener("beforeunload", handler);
  // }, []);

  return (
    <AuthContext.Provider
      value={{
        bearerToken,
        userData,
        updateUserData,
        login,
        logout,
        registerBiometric,
        prepareBiometricChallenge,
        biometricLogin,
        resetActivity,
        status,
        setStatus: handleStatusChange,
        redirectPath,
        setRedirectPath,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => useContext(AuthContext);
